'use client';

// Phase E §E.2.5 — client form for /age-verify.
//
// Posts multipart/form-data to /api/viewer-age-gate/verify (or /verify-anon
// when the FL viewer opts into the anonymized path per Florida HB 3
// statute). The BFF route at /api/viewer-age-gate/[[...path]] forwards
// to the api with the forensic headers preserved (X-Original-Client-Ip
// per the §2257-IP-capture pattern).
//
// All rejection responses render the SAME generic message — the form
// does NOT branch UI on rejection reason (underage vs face-mismatch vs
// rate-limit vs processing) per the ADR §E.1 #3.

import React from 'react';
import { useRouter } from 'next/navigation';
import { Upload, Camera, ShieldCheck, AlertCircle } from 'lucide-react';

type GatedState = { code: string; name: string };

const GENERIC_REJECTION =
  'Verification could not be completed. Please try again or contact support at support@juicystarz.com.';

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB each — api caps combined at 20MB

export default function AgeVerifyForm({
  gatedStates,
  detectedState,
  returnUrl,
}: {
  gatedStates: ReadonlyArray<GatedState>;
  detectedState: string | null;
  returnUrl: string;
}) {
  const router = useRouter();
  const [state, setState] = React.useState<string>(detectedState ?? '');
  const [idFront, setIdFront] = React.useState<File | null>(null);
  const [selfie, setSelfie] = React.useState<File | null>(null);
  const [useAnon, setUseAnon] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [remaining, setRemaining] = React.useState<number | null>(null);

  // Florida is the only state that exposes the anonymized-mode option.
  const showAnonOption = state === 'FL';

  // Best-effort remaining-attempts hint. We don't surface a lockout
  // countdown (timing side-channel); just a friendly "you have N tries
  // left" if N is small.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/viewer-age-gate/status', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && typeof data.remainingAttempts === 'number') {
          setRemaining(data.remainingAttempts);
        }
      } catch { /* ignore — status hint is non-essential */ }
    })();
    return () => { cancelled = true; };
  }, []);

  function validateClientSide(): string | null {
    if (!useAnon && !state) return 'Select your state.';
    if (!idFront) return 'Add a photo of your ID.';
    if (!selfie) return 'Add a current selfie.';
    if (idFront.size > MAX_FILE_BYTES) return 'Your ID photo is too large (max 10MB).';
    if (selfie.size > MAX_FILE_BYTES) return 'Your selfie is too large (max 10MB).';
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validateClientSide();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('idFront', idFront!);
      fd.append('selfie', selfie!);
      if (!useAnon) fd.append('state', state);

      const path = useAnon ? 'verify-anon' : 'verify';
      const res = await fetch(`/api/viewer-age-gate/${path}`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data?.ok) {
        // Cookie is set by api → browser; safe to navigate.
        router.replace(returnUrl || '/');
        return;
      }
      // All non-ok responses render the same generic message.
      setError(GENERIC_REJECTION);
      // Refresh remaining-attempts hint so the user can decide whether
      // to retry. Failures don't reset the counter; successes do.
      try {
        const sres = await fetch('/api/viewer-age-gate/status', { cache: 'no-store' });
        if (sres.ok) {
          const sd = await sres.json();
          if (typeof sd.remainingAttempts === 'number') setRemaining(sd.remainingAttempts);
        }
      } catch { /* ignore */ }
    } catch {
      setError(GENERIC_REJECTION);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-card border rounded-lg p-6 space-y-5 shadow-sm">
      {/* State selector — hidden in anon mode since FL anon flow doesn't record state */}
      {!useAnon && (
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="state">
            Your state
          </label>
          <select
            id="state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
            required={!useAnon}
          >
            <option value="">Select state…</option>
            {gatedStates.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Florida anonymized-mode opt-in */}
      {showAnonOption && (
        <label className="flex items-start gap-3 text-sm bg-muted/40 border rounded-md p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={useAnon}
            onChange={(e) => setUseAnon(e.target.checked)}
            className="mt-0.5"
          />
          <div>
            <div className="font-medium">Verify anonymously (Florida)</div>
            <div className="text-muted-foreground text-xs mt-0.5">
              Florida law lets you verify without recording your state. Your ID is still checked against your selfie; no state-level audit row is written.
            </div>
          </div>
        </label>
      )}

      {/* ID front upload */}
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="idFront">
          Photo of your government-issued ID (front)
        </label>
        <div className="flex items-center gap-3">
          <Upload className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
          <input
            id="idFront"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setIdFront(e.target.files?.[0] ?? null)}
            className="flex-1 text-sm"
            required
          />
        </div>
        {idFront && (
          <div className="text-xs text-muted-foreground mt-1">{idFront.name} · {(idFront.size / 1024).toFixed(0)}KB</div>
        )}
      </div>

      {/* Selfie capture */}
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="selfie">
          Current selfie
        </label>
        <div className="flex items-center gap-3">
          <Camera className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden />
          <input
            id="selfie"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="user"
            onChange={(e) => setSelfie(e.target.files?.[0] ?? null)}
            className="flex-1 text-sm"
            required
          />
        </div>
        {selfie && (
          <div className="text-xs text-muted-foreground mt-1">{selfie.name} · {(selfie.size / 1024).toFixed(0)}KB</div>
        )}
      </div>

      {/* Error surface */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-3">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" aria-hidden />
          <div>{error}</div>
        </div>
      )}

      {/* Remaining-attempts hint (only when ≤ 2) */}
      {remaining !== null && remaining <= 2 && remaining > 0 && (
        <div className="text-xs text-muted-foreground">
          You have {remaining} {remaining === 1 ? 'attempt' : 'attempts'} remaining.
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2.5 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <ShieldCheck className="w-4 h-4" aria-hidden />
        {submitting ? 'Verifying…' : 'Verify my age'}
      </button>
    </form>
  );
}
